/**
 * Data hooks.
 *
 * Every query key starts with the user id, and that is not decoration. Without it, signing out and
 * signing in as someone else leaves the previous account's places in the cache — TanStack Query would
 * serve them from memory for `staleTime` and the new user would see the old user's data. On a shared
 * device that is a privacy failure, not a stale-data annoyance. Including the id makes the switch a
 * different key, so the old entries are simply not candidates.
 *
 * Mutations invalidate the keys they affect rather than writing the result into the cache. Optimistic
 * updates are the faster choice and the wrong one here: the numbers a list renders are the server's —
 * a plan's distances and driving times are measured there — so a locally-guessed value would be visibly
 * corrected a moment later, which looks like a bug in a list the user is reading.
 */

import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { ApiError } from "@trippool/api-client";
import type { PlaceCategory } from "@trippool/contracts";

import { useSessionManager } from "./session-context.tsx";

/** The keys, in one place so an invalidation cannot miss one by typo. */
export const queryKeys = {
  me: (userId: string) => ["me", userId] as const,
  sessions: (userId: string) => ["sessions", userId] as const,
  places: (userId: string, search: string) => ["places", userId, search] as const,
  /**
   * Provider name search. Its own key space, separate from `places`, because it spends a rate-limited
   * upstream's quota rather than reading the database — and the two must never be invalidated together.
   */
  placeLookup: (userId: string, q: string, centre: string) => ["placeLookup", userId, q, centre] as const,
  place: (userId: string, placeId: string) => ["place", userId, placeId] as const,
  /**
   * The saved duplicate pairs. Its own key, invalidated by the merge: a merge changes which pairs
   * exist, and a cached list would otherwise keep offering a pair whose duplicate no longer exists.
   */
  duplicatePairs: (userId: string) => ["duplicatePairs", userId] as const,
  areas: (userId: string) => ["areas", userId] as const,
  /** Provider search. Kept apart from `places` because it hits a rate-limited service. */
  trips: (userId: string) => ["trips", userId] as const,
  trip: (userId: string, tripId: string) => ["trip", userId, tripId] as const,
  tripPlans: (userId: string, tripId: string) => ["tripPlans", userId, tripId] as const,
  tripPlan: (userId: string, tripId: string, variantId: string) =>
    ["tripPlan", userId, tripId, variantId] as const,
  /**
   * The trip fund. One key for the whole response — totals, participants and entries — because the pot,
   * the panel and the list are one read of the same rows, and invalidating them apart would let the
   * header disagree with the list it summarises.
   */
  fund: (userId: string, tripId: string) => ["tripFund", userId, tripId] as const,
  /** A road for a set of points. Kept apart from the plan keys: nothing stores it. */
  routePreview: (userId: string, points: string) => ["routePreview", userId, points] as const,
};

/**
 * How long the arrange step waits before asking for a road.
 *
 * The provider is the reason, not the browser: every settled arrangement is one upstream routing request,
 * and the arrangement changes on every tap. Measured against a screen full of ticks, 400ms is longer than
 * the pause inside a series of deliberate taps and shorter than the pause before reading the answer.
 */
export const ROUTE_PREVIEW_DEBOUNCE_MS = 400;

/** The points as a cache key and as the debounce's own subject — one spelling, so the two cannot disagree. */
function pointsKey(points: ReadonlyArray<{ lat: number; lon: number }>): string {
  // Fixed precision: the same place always serializes the same way, and two coordinates that differ in the
  // sixth decimal are the same street corner.
  return points.map((point) => `${point.lat.toFixed(5)},${point.lon.toFixed(5)}`).join("|");
}

/**
 * The points, once the user has stopped changing them.
 *
 * The timer is keyed on the *coordinates* and not on the array: a screen that re-renders for its own
 * reasons hands down a new array with the same contents, and a timer that restarted on identity would
 * restart forever on a busy screen and never fire.
 *
 * The pair that comes back is a pair — the settled key with the points that produced it. Returning the new
 * points under the old key would ask the provider about a day the user has already changed.
 */
export function useSettledPoints(
  points: ReadonlyArray<{ lat: number; lon: number }>,
  delayMs: number,
): ReadonlyArray<{ lat: number; lon: number }> {
  const key = pointsKey(points);
  const [settled, setSettled] = useState(() => ({ key, points }));
  const latest = useRef(points);
  latest.current = points;

  useEffect(() => {
    if (key === settled.key) return;
    const timer = setTimeout(() => setSettled({ key, points: latest.current }), delayMs);
    return () => clearTimeout(timer);
  }, [key, settled.key, delayMs]);

  return settled.key === key ? points : settled.points;
}

/**
 * The road through a day the user is arranging.
 *
 * `fixed` is passed through rather than decided here: it is the user's statement about the day — they gave
 * its stops times — and the caller is the one that knows.
 */
export function useRoutePreview(
  userId: string,
  points: ReadonlyArray<{ lat: number; lon: number }>,
  fixed: boolean,
  enabled: boolean,
) {
  const { client } = useSessionManager();
  const settled = useSettledPoints(points, ROUTE_PREVIEW_DEBOUNCE_MS);
  return useQuery({
    queryKey: queryKeys.routePreview(userId, `${fixed ? "fixed" : "optimal"}:${pointsKey(settled)}`),
    queryFn: () => client.previewRoute({ points: settled.map((point) => ({ lat: point.lat, lon: point.lon })), fixed }),
    // A road needs somewhere to go, and no answer exists for one point — asking would be a request the
    // contract refuses.
    enabled: enabled && settled.length >= 2,
  });
}

export function useSessions(userId: string, enabled = true) {
  const { client } = useSessionManager();
  return useQuery({
    queryKey: queryKeys.sessions(userId),
    queryFn: () => client.listSessions(),
    // The session list is one of the few things that changes without this client doing it — the user
    // may have signed in on another device a moment ago.
    refetchOnWindowFocus: true,
    // Public mode has no session list to show and no per-device sessions to list, so the request is
    // not made at all — a screen does not read what it does not render.
    enabled,
  });
}

export function useRevokeSession(userId: string) {
  const { client } = useSessionManager();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) => client.revokeSession({ id: sessionId }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.sessions(userId) });
    },
  });
}

/** True when the failure is a 401, so a screen can route to sign-in instead of showing an error. */
export function isUnauthenticated(error: unknown): boolean {
  return (error as ApiError | null)?.status === 401;
}

/**
 * The filters the manage list can apply, all server-side.
 *
 * One object rather than a list of positional arguments, and it is serialized into the query key: two
 * different filter sets must not share a cache entry, and a key built from only `q` would serve the
 * unfiltered page to a filtered screen.
 */
export interface PlaceFilters {
  q?: string;
  limit?: number;
  category?: PlaceCategory;
  areaId?: string;
  hasLocation?: boolean;
}

/** A stable string for a filter set, so the query key changes exactly when the request would. */
function placeFiltersKey(filters: PlaceFilters): string {
  return [
    filters.q ?? "",
    filters.category ?? "",
    filters.areaId ?? "",
    filters.hasLocation === undefined ? "" : String(filters.hasLocation),
  ].join("\u0000");
}

/** The user's saved places, with the folded search and every filter applied server-side. */
export function usePlaces(userId: string, filters: PlaceFilters = {}, enabled = true) {
  const { client } = useSessionManager();
  return useQuery({
    queryKey: queryKeys.places(userId, placeFiltersKey(filters)),
    /*
     * `enabled` exists for the capture form, which reads the list only to infer a nearby area once the
     * user has placed a coordinate — so a form opened without one does not pay for a list it will not
     * read. Everywhere else it defaults to true and behaves as before.
     */
    enabled: enabled && userId !== "",
    queryFn: () =>
      client.listPlaces({
        limit: filters.limit ?? 100,
        ...(filters.q !== undefined ? { q: filters.q } : {}),
        ...(filters.category !== undefined ? { category: filters.category } : {}),
        ...(filters.areaId !== undefined ? { area_id: filters.areaId } : {}),
        // The contract's enum, not a boolean: a query string has no booleans, and `false` sent as a
        // boolean would serialize to `false` and be read as a string the schema rejects.
        ...(filters.hasLocation !== undefined
          ? { has_location: filters.hasLocation ? ("true" as const) : ("false" as const) }
          : {}),
      }),
  });
}

export function usePlace(userId: string, placeId: string) {
  const { client } = useSessionManager();
  return useQuery({
    queryKey: queryKeys.place(userId, placeId),
    queryFn: () => client.getPlace({ id: placeId }),
  });
}

/** The caller's area vocabulary, for the capture form's tag field. */
export function useAreas(userId: string) {
  const { client } = useSessionManager();
  return useQuery({
    queryKey: queryKeys.areas(userId),
    queryFn: () => client.listAreas(),
    // The vocabulary changes only when this user adds a word, and it is small enough to keep.
    staleTime: 5 * 60_000,
  });
}

/**
 * The user's own places matching a query, for the capture form's suggestions.
 *
 * Deliberately not a provider call. The contract's order is own-places-first because the user's saved
 * knowledge is the asset, and a suggestion list that went to a rate-limited upstream on every
 * keystroke would break against Nominatim's one-request-per-second policy — measured, a prefix query
 * does not work there at all ("Thác Da" hits, "Thác Datan" returns nothing), so the list would go
 * *empty* as the user typed more of the word.
 */
export function useOwnPlaceSuggestions(userId: string, text: string, enabled: boolean) {
  const { client } = useSessionManager();
  const trimmed = text.trim();
  return useQuery({
    queryKey: queryKeys.places(userId, `suggest:${trimmed}`),
    queryFn: () => client.listPlaces({ q: trimmed, limit: 6 }),
    enabled: enabled && trimmed.length >= 2,
    // Keystrokes share a key while the query settles, so typing does not fire a request per character.
    staleTime: 30_000,
  });
}

/**
 * Provider name search, for suggestions and coordinates.
 *
 * The centre is a parameter rather than read from a store, because a query keyed on a global would serve
 * one screen's answers to another in a different city. The key includes it for the same reason.
 *
 * `retry: false` is deliberate. The endpoint answers 503 when its upstream is unreachable and the
 * client's default policy would retry that three times — for a suggestion list the user is still typing
 * into, three retries spend the rate-limited quota to produce the same "unavailable", and the field
 * already has something useful to say.
 */
export function usePlaceLookup(
  userId: string,
  text: string,
  centre: { lat: number; lon: number },
  enabled: boolean,
) {
  const { client } = useSessionManager();
  const trimmed = text.trim();
  const centreKey = `${centre.lat.toFixed(5)},${centre.lon.toFixed(5)}`;
  return useQuery({
    queryKey: queryKeys.placeLookup(userId, trimmed, centreKey),
    queryFn: () => client.lookupPlace({ q: trimmed, lat: centre.lat, lon: centre.lon }),
    enabled: enabled && trimmed.length >= 2,
    // The same word retyped a moment later is the same answer, and the provider is what it costs.
    staleTime: 60_000,
    retry: false,
  });
}

/**
 * AI place lookup with Gemini and Google Search Grounding.
 */
export function useAiPlaceLookup() {
  const { client } = useSessionManager();
  return useMutation({
    mutationFn: (vars: { query: string; area_hint?: string; api_key?: string }) =>
      client.aiLookupPlace(vars),
  });
}

/**
 * Create a place.
 *
 * No optimistic update. The server assigns the id and the timestamps, and every list this place will
 * appear in is asked again rather than patched, so nothing on screen names a value the server did not
 * return.
 *
 * **The caller supplies the idempotency key, and that is the point of the argument.** The client would
 * otherwise generate a fresh UUID per call, which collapses a *retry* of one request but not two
 * submissions of one form: a double tap sends two calls, two generated keys, and the server — which
 * can only collapse what carries the same key — writes two places. Passing one key for the whole form
 * is what makes both submissions one save, and the key has to be rotated by the form when it starts a
 * genuinely new place, which is why it is a parameter and not something this hook invents.
 */
export function useCreatePlace(userId: string) {
  const { client } = useSessionManager();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { body: Parameters<typeof client.createPlace>[0]; idempotencyKey: string }) =>
      client.createPlace(vars.body, { idempotencyKey: vars.idempotencyKey }),
    onSuccess: async () => {
      /*
       * The two-element prefix, not `queryKeys.places(userId, "")`.
       *
       * A list query's key carries a filter string (`placeFiltersKey`), and for an unfiltered list that
       * string is the three NUL separators, not `""` — so the old key matched no query at all and a
       * create invalidated nothing. It only looked right because the list refetches on mount, which is
       * fast on a laptop and not something to rely on: measured in the browser, a place saved and then
       * listed on `/places` stayed absent from the list for as long as it was watched. The prefix
       * invalidates every list variant, which is exactly what the update and delete hooks below already do.
       */
      await queryClient.invalidateQueries({ queryKey: ["places", userId] });
    },
  });
}

/** Add an area, or get the existing one. The server owns the idempotency, not the client. */
export function useCreateArea(userId: string) {
  const { client } = useSessionManager();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => client.createArea({ name }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.areas(userId) });
    },
  });
}

/**
 * Edit a place.
 *
 * Invalidates every `places` key rather than the one being looked at, because `q` is part of the key:
 * a renamed place must stop appearing under its old name in a filtered list, and patching one cache
 * entry would leave the search results disagreeing with the list beside them.
 */
export function useUpdatePlace(userId: string, placeId: string) {
  const { client } = useSessionManager();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof client.updatePlace>[1]) => client.updatePlace({ id: placeId }, body),
    onSuccess: async (place) => {
      queryClient.setQueryData(queryKeys.place(userId, placeId), place);
      await queryClient.invalidateQueries({ queryKey: ["places", userId] });
    },
  });
}

/**
 * Delete a place.
 *
 * The response reports what else it touched — `affected_itinerary_items` and `orphaned_areas` — and
 * both are surfaced by the caller rather than ignored, because a delete that silently breaks a trip
 * or leaves a dead area in the vocabulary is a delete the user cannot undo.
 */
export function useDeletePlace(userId: string) {
  const { client } = useSessionManager();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (placeId: string) => client.deletePlace({ id: placeId }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["places", userId] });
    },
  });
}

/**
 * The pre-save duplicate check.
 *
 * A mutation rather than a query, and deliberately: the input is a *draft* that has no cache identity,
 * and the answer must be the server's at the moment the user presses Lưu rather than a list cached
 * from a keystroke ago. Nothing invalidates on success — the check reads and writes nothing.
 */
export function useDuplicateCheck() {
  const { client } = useSessionManager();
  return useMutation({
    mutationFn: (input: { name: string; lat: number; lon: number }) => client.checkPlaceDuplicates(input),
  });
}

/**
 * The pairs already saved that the rule can see.
 *
 * **Retained without a caller, deliberately.** The manage list's manual review section was removed, so
 * nothing in the app navigates here today — but `GET /places/duplicates` stays published (removing a
 * published route is a breaking change that needs a version bump), and this hook is its client half. It
 * is exactly what a merge reached from a place's own card would read, which is the follow-up the
 * removal leaves open: the rule provably misses some pairs, so an existing one has no UI path to merge
 * until that card-level surface exists.
 *
 * `enabled` stays because a future caller must be able to gate the scan: it is one query, but it reads
 * every one of the caller's located places through the rule, so it should run on request, not on every
 * list visit. A partial list by nature — see the contract.
 */
export function useDuplicatePairs(userId: string, enabled: boolean) {
  const { client } = useSessionManager();
  return useQuery({
    queryKey: queryKeys.duplicatePairs(userId),
    queryFn: () => client.listDuplicatePairs(),
    enabled: enabled && userId !== "",
  });
}

/**
 * Merge one place into another.
 *
 * Both the place list and the pair list are invalidated: the merge soft-deletes the duplicate, so the
 * list changes, and it removes the pair, so the pair list must not keep offering it.
 */
export function useMergePlaces(userId: string) {
  const { client } = useSessionManager();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { keepId: string; duplicateId: string }) =>
      client.mergePlace({ id: input.keepId }, { duplicate_place_id: input.duplicateId }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["places", userId] }),
        queryClient.invalidateQueries({ queryKey: queryKeys.duplicatePairs(userId) }),
      ]);
    },
  });
}

/**
 * The caller's trips.
 *
 * `limit: 100` and no paging control, which is a deliberate ceiling rather than an oversight: this list
 * is a door into each trip, and the API has a cursor for when someone owns more than a hundred. A
 * "load more" that no user has needed yet is the kind of control that looks finished and is not.
 */
export function useTrips(userId: string) {
  const { client } = useSessionManager();
  return useQuery({
    queryKey: queryKeys.trips(userId),
    queryFn: () => client.listTrips({ limit: 100 }),
  });
}

export function useTrip(userId: string, tripId: string) {
  const { client } = useSessionManager();
  return useQuery({
    queryKey: queryKeys.trip(userId, tripId),
    queryFn: () => client.getTrip({ id: tripId }),
    // A 404 is final: retrying a trip that was deleted only delays saying so.
    retry: false,
    /*
     * The trip id is a route parameter, and the route that *reads* a trip is not the only one that mounts
     * this hook — the planner asks for a trip only when it is editing one. Without the guard, opening the
     * planner to make a new trip fired `GET /trips/` with an empty id and answered 404, which is a request
     * for nothing and an error the screen then had to know to ignore.
     */
    enabled: userId !== "" && tripId !== "",
  });
}

/**
 * Delete a trip.
 *
 * The server soft-deletes: the trip, its plans, its nights and its arrangement stop being reachable, and
 * the places it referenced are untouched — a trip is a view over places it does not own. Invalidation
 * follows that: the trip list loses a row and the trip's own queries are dropped, but nothing about places
 * changes.
 */
/**
 * Renaming, as one field.
 *
 * `PATCH /trips/:id` leaves an omitted field alone, which is what makes this cheap: a rename sends the title
 * and nothing else. The alternative — read the trip, change the title, write the whole thing back — would
 * carry the places, the nights and the arrangement through a round trip, and anything edited in another tab
 * in between would be overwritten by the copy this one was holding.
 */
export function useRenameTrip(userId: string) {
  const { client } = useSessionManager();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; title: string }) =>
      client.updateTrip({ id: input.id }, { title: input.title }),
    onSuccess: async (_result, input) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.trips(userId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.trip(userId, input.id) }),
      ]);
    },
  });
}

/**
 * A copy, made from what the server already holds.
 *
 * Two calls and no endpoint of its own: the detail returns every field `POST /trips` accepts, so a copy is
 * the trip it was made from — its dates, its nights, its places and the user's own arrangement — created
 * again under a title the caller supplies.
 *
 * What it deliberately does **not** carry is the plans. A plan was generated for one trip from one set of
 * inputs, and a copy that arrived holding a route nobody chose for it would be a claim the copy cannot
 * support. The copy is a draft, which is what the API calls a trip before a plan is selected.
 */
export function useDuplicateTrip(userId: string) {
  const { client } = useSessionManager();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; title: string }) => {
      const source = await client.getTrip({ id: input.id });
      const { trip, constraints, place_ids, nights, place_days, rest_days } = source;
      return client.createTrip({
        title: input.title,
        destination: trip.destination,
        start_date: trip.start_date,
        end_date: trip.end_date,
        adults: trip.adults,
        children: trip.children,
        transport: trip.transport,
        transport_note: trip.transport_note,
        constraints,
        place_ids,
        stay_last_night: trip.stay_last_night,
        entry_place_id: trip.entry_place_id,
        nights: nights.map((night) => ({ night_date: night.night_date, place_id: night.place_id })),
        // Spread rather than an explicit `undefined`: an absent time slot is a different fact from a null
        // one, and `exactOptionalPropertyTypes` treats writing the key as a claim about it.
        place_days: place_days.map((day) => ({
          place_id: day.place_id,
          day_index: day.day_index,
          ...(day.time_slot === undefined || day.time_slot === null ? {} : { time_slot: day.time_slot }),
        })),
        rest_days,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.trips(userId) });
    },
  });
}

export function useDeleteTrip(userId: string) {
  const { client } = useSessionManager();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tripId: string) => client.deleteTrip({ id: tripId }),
    onSuccess: async (_result, tripId) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.trips(userId) }),
        queryClient.removeQueries({ queryKey: queryKeys.trip(userId, tripId) }),
        queryClient.removeQueries({ queryKey: queryKeys.tripPlans(userId, tripId) }),
      ]);
    },
  });
}

/**
 * The plans a trip already has.
 *
 * `enabled` is not an optimisation. The trip id comes from the URL, so the first render of that route
 * can run before a session has been restored, and firing anyway would spend a request on a 401 — one
 * the user would see as a failed screen rather than a slow one.
 */
export function useTripPlans(userId: string, tripId: string) {
  const { client } = useSessionManager();
  return useQuery({
    queryKey: queryKeys.tripPlans(userId, tripId),
    queryFn: () => client.listPlanVariants({ id: tripId }),
    enabled: userId !== "" && tripId !== "",
  });
}

/** One variant in full, fetched only once there is a variant to ask for. */
export function useTripPlan(userId: string, tripId: string, variantId: string) {
  const { client } = useSessionManager();
  return useQuery({
    queryKey: queryKeys.tripPlan(userId, tripId, variantId),
    queryFn: () => client.getPlanVariant({ id: tripId, variantId }),
    enabled: userId !== "" && tripId !== "" && variantId !== "",
  });
}

/**
 * Generate the three strategies for an existing trip.
 *
 * Separate from the planner's own mutation of the same endpoint, and that is the difference that
 * matters: this one is called on a trip that already exists and needs no step state to interpret the
 * result. The planner's version reads the result into its own state — the variants, their days and their
 * reasons — and it is the step state that decides which of them is shown; this screen has no step to move.
 *
 * `place_ids` is not sent. Regenerating an empty trip answers 422, and the honest place to fix a trip
 * with no places is the place selection the planner already has — so this screen says what is missing
 * and links there rather than silently planning nothing.
 */
export function useGeneratePlans(userId: string, tripId: string) {
  const { client } = useSessionManager();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => client.createPlans({ id: tripId }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.tripPlans(userId, tripId) }),
        // The trip's own row carries the plan count and the status, and `listTrips` shows both.
        queryClient.invalidateQueries({ queryKey: queryKeys.trip(userId, tripId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.trips(userId) }),
      ]);
    },
  });
}

/*
 * The trip fund.
 *
 * Every mutation invalidates the fund key rather than writing a figure into the cache. The pot, the
 * per-member totals and each row's after-figure are the server's own arithmetic over the rows it just
 * changed; a value computed on this side would be a second sum that could disagree with the list beside
 * it, and the disagreement would be money.
 *
 * `null` is never sent as a stand-in for "absent": a day the user left empty is an omitted field, which
 * the API reads as "no day", and an explicit null on an edit clears one. The two are different facts and
 * the API's schemas say so.
 */

export interface FundContributionInput {
  participant_id: string;
  amount_vnd: number;
  day_index?: number | null;
  note?: string | null;
}

export interface FundExpenseInput {
  description: string;
  amount_vnd: number;
  day_index?: number | null;
  note?: string | null;
}

export interface FundEntryPatch {
  amount_vnd?: number;
  description?: string;
  day_index?: number | null;
  note?: string | null;
}

export function useTripFund(userId: string, tripId: string, enabled = true) {
  const { client } = useSessionManager();
  return useQuery({
    queryKey: queryKeys.fund(userId, tripId),
    queryFn: () => client.getTripFund({ id: tripId }),
    enabled: enabled && userId !== "" && tripId !== "",
  });
}

export function useCreateFundParticipant(userId: string, tripId: string) {
  const { client } = useSessionManager();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => client.createFundParticipant({ id: tripId }, { name }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.fund(userId, tripId) });
    },
  });
}

export function useUpdateFundParticipant(userId: string, tripId: string) {
  const { client } = useSessionManager();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { participantId: string; name: string }) =>
      client.updateFundParticipant({ id: tripId, participantId: input.participantId }, { name: input.name }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.fund(userId, tripId) });
    },
  });
}

export function useRemoveFundParticipant(userId: string, tripId: string) {
  const { client } = useSessionManager();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (participantId: string) => client.deleteFundParticipant({ id: tripId, participantId }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.fund(userId, tripId) });
    },
  });
}

export function useRecordFundContribution(userId: string, tripId: string) {
  const { client } = useSessionManager();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: FundContributionInput) => client.recordFundContribution({ id: tripId }, input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.fund(userId, tripId) });
    },
  });
}

export function useRecordFundExpense(userId: string, tripId: string) {
  const { client } = useSessionManager();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: FundExpenseInput) => client.recordFundExpense({ id: tripId }, input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.fund(userId, tripId) });
    },
  });
}

export function useUpdateFundContribution(userId: string, tripId: string) {
  const { client } = useSessionManager();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { entryId: string } & FundEntryPatch) => {
      const { entryId, ...patch } = input;
      return client.updateFundContribution({ id: tripId, entryId }, patch);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.fund(userId, tripId) });
    },
  });
}

export function useUpdateFundExpense(userId: string, tripId: string) {
  const { client } = useSessionManager();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { entryId: string } & FundEntryPatch) => {
      const { entryId, ...patch } = input;
      return client.updateFundExpense({ id: tripId, entryId }, patch);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.fund(userId, tripId) });
    },
  });
}

export function useDeleteFundContribution(userId: string, tripId: string) {
  const { client } = useSessionManager();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (entryId: string) => client.deleteFundContribution({ id: tripId, entryId }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.fund(userId, tripId) });
    },
  });
}

export function useDeleteFundExpense(userId: string, tripId: string) {
  const { client } = useSessionManager();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (entryId: string) => client.deleteFundExpense({ id: tripId, entryId }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.fund(userId, tripId) });
    },
  });
}
